/**
 * Helper functions for product selection and dependency management
 */

/**
 * Checks if a product has color options
 */
export function hasColorOptions(materialSchema: any[], productCategory: string, productName: string): boolean {
    const categorySchema = materialSchema.find((schemaItem: any) => schemaItem.Name === productCategory);
    const selectedProductSubcategories =
        categorySchema?.Subcategories?.filter((sub: any) => sub.Name === productName) || [];
    return selectedProductSubcategories.some((sub: any) => sub.Color);
}

/**
 * Finds manufacturerProductId from materialSchema for a given category and product name
 */
export function findManufacturerProductId(
    materialSchema: any[],
    category: string,
    productName: string
): string | undefined {
    if (!materialSchema || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categorySchema = materialSchema.find((schemaItem: any) => {
        const name = (schemaItem.Name || schemaItem.ProductName || schemaItem.name || "").trim().toLowerCase();
        return name === category.trim().toLowerCase();
    });

    if (!categorySchema || !categorySchema.Subcategories) {
        return undefined;
    }

    // Find the first subcategory that matches the product name (case-insensitive)
    const normalizedProductName = productName.trim().toLowerCase();
    const matchingSubs = categorySchema.Subcategories.filter((sub: any) => 
        (sub.Name || "").trim().toLowerCase() === normalizedProductName
    );

    if (matchingSubs.length === 0) {
        return undefined;
    }

    // Prefer subcategory without color (default), otherwise return first match
    const defaultSub = matchingSubs.find((sub: any) => !sub.Color);
    return defaultSub?.Id || matchingSubs[0]?.Id;
}

/**
 * Recursively sets dependencies for a selected product
 */
export function setDependencies(
    dependencies: any[],
    tempStateData: any,
    category: string,
    product: string,
    materialSchema: any[],
    measurements: any[]
): void {
    const deps =
        dependencies
            .find((dep: any) => dep.Category === category)
            ?.Mappings?.find((map: any) => map.Product === product)?.RequiredProducts || [];

    for (const dep of deps) {
        // 1. Find the specific manufacturer ID for this dependency
        const manufacturerProductId = findManufacturerProductId(materialSchema, dep.Category, dep.Product);
        
        // 2. NEW: Calculate the quantity for this dependent item dynamically
        const calculatedQuantity = calculateQuantity(materialSchema, measurements, dep.Category, dep.Product);

        // 3. Update the state for the dependent category
        tempStateData[dep.Category] = {
            value: dep.Product,
            quantity: calculatedQuantity,
            manufacturerProductId,
        };

        // 4. Recursively set nested dependencies
        setDependencies(dependencies, tempStateData, dep.Category, dep.Product, materialSchema, measurements);
    }
}

/**
 * Derives the estimated quantity for a product based on measurements and coverage
 */
export function calculateQuantity(
    materialSchema: any[],
    measurements: any[],
    category: string,
    productName: string
): number {
    if (!materialSchema || !measurements || !Array.isArray(measurements)) return 1;

    // 1. Find the category and specific product metadata (to get UnitCoverage and MeasurementType)
    const categoryInfo = materialSchema.find((item: any) => (item.Name || item.ProductName) === category);
    
    // Get the measurement types required for this category (DYNAMICALY FROM SCHEMA)
    const requiredMeasurementTypes = categoryInfo?.MeasurementType?.map((m: any) => m.Type) || ["Area"];
    
    const sub = categoryInfo?.Subcategories?.find((s: any) => s.Name === productName);
    const coverage = Number(sub?.UnitCoverage || 1);

    // 2. Sum up ONLY the relevant measurements defined for this category
    let totalMeasurementValue = 0;
    requiredMeasurementTypes.forEach((type: string) => {
        const match = measurements.find(m => m.MeasurementType === type);
        totalMeasurementValue += Number(match?.ActualValue || match?.CalculatedValue || 0);
    });

    // 3. Final Calculation: (Measurement / Coverage) rounded UP
    if (totalMeasurementValue === 0 || coverage === 0) return 1;
    
    return Math.ceil(totalMeasurementValue / coverage);
}

/**
 * Derives color from materialSchema based on ManufacturerProductId
 */
export function deriveColorFromSchema(
    materialSchema: any[],
    manufacturerProductId: string | null | undefined,
    productCategory: string
): string | undefined {
    if (!manufacturerProductId || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categoryEntry = materialSchema.find((prod: any) => {
        const name = prod.Name || prod.ProductName || prod.name;
        return name === productCategory;
    });

    const sub = categoryEntry?.Subcategories?.find((s: any) => s.Id === manufacturerProductId && s.Color);
    return sub?.Color as string | undefined;
}
